import { useState } from "react";

import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "@emailjs/browser";

import { partnershipTypes } from "./faqData";

// Shared lead-capture step for the chat assistant. `leadType` switches copy and
// which fields are required, but both intents send through the SAME EmailJS
// service/template that containers/home/Form.jsx already uses, so no dashboard
// changes are needed. The intent is folded into `question` (visible in any
// template) and also passed as `lead_type`/`subject` for teams that later add
// {{lead_type}} to the template.
const LeadForm = ({ leadType = "inquiry", onSent }) => {
  const isPartnership = leadType === "partnership";

  const initial = {
    name: "",
    company: "",
    email: "",
    number: "",
    partnershipType: partnershipTypes[0],
    message: ""
  };

  const [formData, setFormData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [sendError, setSendError] = useState("");
  const [sending, setSending] = useState(false);

  const sitekey = process.env.REACT_APP_CAPTCHA_KEY; // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (data) => {
    const next = {};

    if (!data.name) {
      next.name = "Name is required";
    } else if (data.name.length < 2) {
      next.name = "Name should be at least 2 characters long";
    } else if (!/^[A-Za-z\s]+$/.test(data.name)) {
      next.name = "Invalid name";
    } else if (data.name.length > 50) {
      next.name = "Name cannot exceed 50 characters.";
    }

    // Company is required for partnership leads, optional for a general
    // question from a site visitor.
    if (isPartnership) {
      if (!data.company) {
        next.company = "Company is required";
      } else if (data.company.length < 2) {
        next.company = "Company should be at least 2 characters long";
      } else if (data.company.length > 50) {
        next.company = "Company cannot exceed 50 characters.";
      }
    } else if (data.company && data.company.length > 50) {
      next.company = "Company cannot exceed 50 characters.";
    }

    if (!data.email) {
      next.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      next.email = "Email is invalid";
    }

    if (data.number) {
      if (!/^\d+$/.test(data.number)) {
        next.number = "Should be an integer";
      } else if (data.number.length < 10) {
        next.number = "Invalid mobile number";
      }
    }

    if (!data.message) {
      next.message = isPartnership
        ? "Tell us a little about the partnership"
        : "Please add your question";
    } else if (data.message.length > 500) {
      next.message = "Please keep this under 500 characters";
    }

    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSendError("");

    const validationErrors = validate(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    if (!recaptchaToken) {
      setRecaptchaError("Please solve the reCAPTCHA.");
      return;
    }

    const typeLabel = isPartnership
      ? "Partnership / Collaboration"
      : "General inquiry";

    // Prefix the message with the intent so it is legible even in the existing
    // template, which only renders {{question}}.
    const context = isPartnership
      ? `[${typeLabel} — ${formData.partnershipType}]`
      : `[${typeLabel}]`;

    const templateParams = {
      from_name: formData.name,
      name: formData.name,
      company: formData.company || "—",
      email: formData.email,
      number: formData.number || "—",
      question: `${context}\n\n${formData.message}`,
      lead_type: typeLabel,
      subject: `${typeLabel} via site assistant`
    };

    setSending(true);
    emailjs
      .send(
        process.env.REACT_APP_EMAIL_JS_SERVICE_KEY, // eslint-disable-line
        process.env.REACT_APP_EMAIL_JS_TEMPLATE_KEY, // eslint-disable-line
        templateParams,
        process.env.REACT_APP_EMAIL_JS_PUBLIC_KEY // eslint-disable-line
      )
      .then(
        () => {
          setSending(false);
          onSent(typeLabel);
        },
        () => {
          setSending(false);
          setSendError("Couldn't send. Please try again.");
        }
      );
  };

  return (
    <form className="chatbot__form" onSubmit={handleSubmit} noValidate>
      <p className="chatbot__form-intro">
        {isPartnership
          ? "Let's explore working together. Share a few details and our team will review and get back to you."
          : "Leave your details and question — this emails our team, who'll review and get back to you."}
      </p>

      <div className="chatbot__field">
        <input
          type="text"
          name="name"
          placeholder="Your name*"
          value={formData.name}
          onChange={handleChange}
        />
        {errors.name && <span className="chatbot__error">{errors.name}</span>}
      </div>

      <div className="chatbot__field">
        <input
          type="text"
          name="company"
          placeholder={isPartnership ? "Company*" : "Company (optional)"}
          value={formData.company}
          onChange={handleChange}
        />
        {errors.company && (
          <span className="chatbot__error">{errors.company}</span>
        )}
      </div>

      <div className="chatbot__field">
        <input
          type="email"
          name="email"
          placeholder="name@company.com*"
          value={formData.email}
          onChange={handleChange}
        />
        {errors.email && <span className="chatbot__error">{errors.email}</span>}
      </div>

      <div className="chatbot__field">
        <input
          type="text"
          name="number"
          placeholder="Mobile number (optional)"
          value={formData.number}
          onChange={handleChange}
        />
        {errors.number && (
          <span className="chatbot__error">{errors.number}</span>
        )}
      </div>

      {isPartnership && (
        <div className="chatbot__field">
          <select
            name="partnershipType"
            value={formData.partnershipType}
            onChange={handleChange}
          >
            {partnershipTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="chatbot__field">
        <textarea
          name="message"
          rows="3"
          placeholder={
            isPartnership
              ? "How would you like to work together?*"
              : "What can we help with?*"
          }
          value={formData.message}
          onChange={handleChange}
        />
        {errors.message && (
          <span className="chatbot__error">{errors.message}</span>
        )}
      </div>

      <div className="chatbot__captcha">
        <ReCAPTCHA
          sitekey={sitekey}
          size="compact"
          onChange={(token) => {
            setRecaptchaToken(token);
            setRecaptchaError("");
          }}
        />
        {recaptchaError && (
          <span className="chatbot__error">{recaptchaError}</span>
        )}
        {sendError && <span className="chatbot__error">{sendError}</span>}
      </div>

      <button type="submit" className="chatbot__send" disabled={sending}>
        {sending
          ? "Sending…"
          : isPartnership
            ? "Send partnership request"
            : "Send to team"}
      </button>
    </form>
  );
};

export default LeadForm;
